import { useState } from "react"
import { mutate } from "swr"
import { FailedDeliveryApi } from "../../../apis"
import type { IReassignInput, IReportFailedInput } from "../../../apis/failed-delivery"

const invalidate = () =>
  mutate(
    (key) => Array.isArray(key) && key[0] === "dn-exceptions",
    undefined,
    { revalidate: true },
  )

export function useFailedMutations() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  async function wrap<T>(fn: () => Promise<T>): Promise<T> {
    setSubmitting(true)
    setError(null)
    try {
      const result = await fn()
      await invalidate()
      return result
    } catch (e) {
      // API failures throw an HttpHandler (not an Error); keep its i18n key instead of "[object Object]".
      const msg = (e as { message?: unknown } | null)?.message
      const err = e instanceof Error ? e : new Error(typeof msg === "string" && msg ? msg : String(e))
      setError(err)
      throw err
    } finally {
      setSubmitting(false)
    }
  }

  return {
    submitting,
    error,
    reportFailed: (dnId: string, body: IReportFailedInput) =>
      wrap(() => FailedDeliveryApi.reportFailed(dnId, body)),
    confirmAttribution: (
      dnId: string,
      exId: string,
      body: { attribution: string; resolutionNotes?: string | null },
    ) => wrap(() => FailedDeliveryApi.confirmAttribution(dnId, exId, body)),
    reassign: (dnId: string, body: IReassignInput) =>
      wrap(() => FailedDeliveryApi.reassign(dnId, body)),
  }
}
